import { FastAverageColor } from "fast-average-color";
import React from "react";
import DOMPurify from "dompurify";

// Helper function to check if media is video
export const isVideo = (media) => {
  if (!media) return false;
  return media.type === "video";
};

// Helper function for text formatting (keeping from original)
const boldText = (text, targetStrings) => {
  if (typeof text !== "string") {
    console.warn("Expected a string for text, but received:", text);
    return text;
  }

  // First, preserve paragraph breaks by replacing them with a special marker
  let formattedText = text.replace(/\n\n/g, "{{PARAGRAPH_BREAK}}");

  // Apply bold formatting
  targetStrings.forEach((target) => {
    const regex = new RegExp(`(${target})`, "gi");
    formattedText = formattedText.replace(
      regex,
      "<br/><strong>$1</strong><br/>"
    );
  });

  // Restore paragraph breaks with proper HTML
  formattedText = formattedText.replace(/{{PARAGRAPH_BREAK}}/g, "</p><p>");

  // Wrap the entire text in paragraphs if not already wrapped
  if (!formattedText.startsWith("<p>")) {
    formattedText = `<p>${formattedText}</p>`;
  }

  return formattedText;
};

// Target strings for bold formatting (keeping from original)
const targetStrings = [
  "You Wish To Accelerate",
  "You Wish To Hover",
  "You Wish To Downshift",
  "You Are A Crossroader",
  "You Are An Urgent Role-Seeker",
  "You Are An Accelerator",
  "You Wish To Remain In Place",
  "You Wish To Switch Industry",
  "You Wish To Switch Roles",
  "You Wish To Try Something Very Different",
  "Have a great career ahead!",
];

// Define the category structure
export const reportCategories = {
  careerOverview: {
    title: "Career Overview",
    cards: ["careerAnimal", "careerHappiness", "careerWellness"],
  },
  navigateCareer: {
    title: "Navigate Your Career",
    cards: [
      "careerStage",
      "careerVelocity",
      "careerDirection",
      "careerStrategy",
      "careerAgility",
    ],
  },
  thrivingAtWork: {
    title: "Thriving At Work",
    cards: [
      "salarySatisfaction",
      "workEnvironmentSatisfaction",
      "workContentSatisfaction",
      "workLifeBalanceSatisfaction",
      "currentWorkloadSatisfaction",
      "jobSecuritySatisfaction",
    ],
  },
  growingCareer: {
    title: "Growing Your Career",
    cards: [
      "skillsClarity",
      "networkingProficiency",
      "careerDisruptionReadiness",
      "linkedInReadiness",
      "cvReadiness",
    ],
  },
};

// Helper functions from original ReportData.js
export const preloadImage = (src) => {
  return new Promise((resolve, reject) => {
    if (!src) {
      console.warn("Attempted to preload image with undefined or null src");
      resolve();
      return;
    }
    const img = new Image();
    img.onload = () => {
      console.log(`Successfully preloaded image: ${src}`);
      resolve(img);
    };
    img.onerror = (error) => {
      console.warn(`Failed to preload image: ${src}`, error);
      resolve();
    };
    img.src = src;
  });
};

export const preloadNextImage = (currentCard, cardData) => {
  if (currentCard < cardData.length - 1) {
    const nextCard = cardData[currentCard + 1];
    if (nextCard && nextCard.media && nextCard.media.src) {
      return preloadImage(nextCard.media.src);
    } else {
      console.warn("Next card or its media source is undefined");
    }
  }
  return Promise.resolve();
};

// Example of career avatar with structured next steps
export const getCareerAvatar = (report) => {
  const avatar = report.careerAvatar;
  const baseMedia = {
    type: "image",
  };

  const avatarData = {
    LION: {
      title: "You are a Lion",
      text: "You are confident in your own Career and you stand tall and proud.\n\nYou are agile and strategic, knowing where the best opportunities are and should the market turn against your favour, you actively seek out new hunting ground with better options.",
      media: { ...baseMedia, src: "/images/Career Avatars/Square - Lion.jpg" },
      nextSteps: [
        {
          title: "Stay Alert",
          body: "Track industry disruptions and emerging trends. Connect with innovators and thought leaders to spot opportunities before others.",
        },
        {
          title: "Evolve Skills",
          body: "Target high-demand future skills through quarterly learning goals. Develop expertise that will be valuable in 2-3 years to maintain your edge.",
        },
        {
          title: "Forge Alliances",
          body: "Build relationships with other Career Lions and influential figures. Create a reciprocal network for insider information and access to premium opportunities.",
        },
      ],
    },
    "T REX": {
      title: "You are a T-Rex",
      text: "Big, bold and brave, you are confident in your own domain and are probably performing well in your job, however, will you continue to be relevant in the years ahead, especially with the ever-disruptive forces affecting our markets?\n\nAre your skillsets still relevant in a changing world and will you still be in demand in the years ahead?",
      media: { ...baseMedia, src: "/images/Career Avatars/Square - T REX.jpg" },
      nextSteps: [
        {
          title: "Close Gaps",
          body: "Identify and address specific skill deficiencies through targeted training. Focus on developing practical capabilities that match your strategic vision.",
        },
        {
          title: "Gain Traction",
          body: "Break down ambitious goals into smaller, achievable steps. Secure early wins to build momentum and demonstrate your growing capabilities.",
        },
        {
          title: "Seek Feedback",
          body: "Actively request input from mentors and leaders on your blind spots. Adjust your approach based on constructive criticism to become more effective.",
        },
      ],
    },
    CANARY: {
      title: "You are a Canary",
      text: "You are highly capable and agile but have you become too comfortable in current role or company?\n\nLike canaries who have lived in cages so long they've forgotten how to fly, you possess valuable skills but might lack a strategic career plan and may hesitate to pursue opportunities outside your comfort zone.",
      media: {
        ...baseMedia,
        src: "/images/Career Avatars/Square - Canary.jpg",
      },
      nextSteps: [
        {
          title: "Spread Wings",
          body: "Challenge your comfort zone by volunteering for projects requiring new skills. Take calculated risks to prove you can thrive beyond your current environment.",
        },
        {
          title: "Stay Alert",
          body: "Track industry disruptions and emerging trends. Connect with innovators and thought leaders to spot opportunities before others.",
        },
        {
          title: "Chart Course",
          body: "Develop a clear career strategy with specific goals and timelines. Map potential paths both inside and outside your current organization to prepare for unexpected changes.",
        },
      ],
    },
    PANDA: {
      title: "You are a Panda",
      text: "Are you someone who has been too comfortable with their career over the years?\n\nLike a cute Panda who finds it difficult to adapt to changes, are you ready for disruptions that could happen in an uncertain job market?",
      media: { ...baseMedia, src: "/images/Career Avatars/Square - Panda.jpg" },
      nextSteps: [
        {
          title: "Embrace Change",
          body: "Acknowledge market realities and commit to adapting. Start with small adjustments to build confidence in your ability to evolve.",
        },
        {
          title: "Upgrade Skills",
          body: "Invest in developing contemporary, in-demand skills. Prioritize learning technologies or methodologies that are essential in your field today.",
        },
        {
          title: "Broaden Horizons",
          body: "Expand your professional perspective by exploring adjacent fields. Connect with peers across different functions to discover new career possibilities and pathways.",
        },
      ],
    },
  };

  return (
    avatarData[avatar] || {
      title: "Career avatar not available",
      text: "",
      media: { ...baseMedia, src: "/images/Career Avatars/default.jpg" },
      nextSteps: [],
    }
  );
};

export const getCareerHappinessScore = (report) => {
  const score = report.overallJobHappinessScore;
  if (score >= 0.5 && score <= 2) {
    return {
      text: "Your results indicate that you seem quite happy in your current role. Congratulations!",
      media: { src: "/images/Overall Job Happiness/Happy.png", type: "image" },
      nextSteps: [
        {
          title: "Pause and Appreciate",
          body: "Take time to understand exactly why you're happy in your current role. This self-awareness will help you maintain happiness through future career changes.",
        },
        {
          title: "Look For Growth Opportunities",
          body: "Consider how you might develop further. This could mean learning new skills, taking on leadership roles, or mentoring others - all while preserving the elements that make your current situation positive.",
        },
        {
          title: "Build Resilience",
          body: "Today's market is uncertain. Maintain your professional network, keep your skills current, and save financially to ensure you're prepared for unexpected disruptions.",
        },
      ],
    };
  } else if (score >= -0.49 && score <= 0.49) {
    return {
      text: "Your results indicate that you feel neutral about your current role. Whilst this is good, you might want to strive towards making it even better.",
      media: { src: "/images/Overall Job Happiness/Happy.png", type: "image" },
      nextSteps: [
        {
          title: "Amplify The Good Parts",
          body: "Identify which aspects of your work bring you joy and find ways to expand them. If you enjoy certain projects, clients, or responsibilities, look for opportunities to increase your involvement in these areas.",
        },
        {
          title: "Experiment With Small Changes",
          body: "Try new approaches to familiar tasks, volunteer for different projects, or collaborate with new colleagues. These small shifts can often bring fresh energy to your work without dramatic changes.",
        },
        {
          title: "Create Meaning Outside Your Job",
          body: "If your work provides reasonable satisfaction but isn't completely fulfilling, develop interests and goals outside of work. Volunteering, hobbies, or side gigs can increase overall life satisfaction.",
        },
      ],
    };
  } else if (score >= -2 && score <= -0.5) {
    return {
      text: "Your results indicate that you may not be as happy in your current role as you would like to be.",
      media: { src: "/images/Overall Job Happiness/Happy.png", type: "image" },
      nextSteps: [
        {
          title: "Identify What's Missing",
          body: "Figure out which aspects of your job are making you unhappy. Refer to the Satisfiers section of this tool to find out more. Once you have identified them, start taking posititve action.",
        },
        {
          title: "Have a Honest Conversation",
          body: "Speak with your manager about possible changes to your role or responsibilities. Many issues can be addressed through open communication.",
        },
        {
          title: "Step Back and Recharge",
          body: "Sometimes, this results from fatigue or burnout. Take some time off to recharge yourself so you can re-centre your mind and energy and gain better clarity on your next career moves.",
        },
      ],
    };
  }
  return "N/A";
};
export const getCareerWellnessScore = (report) => {
  const score = report.overallCareerWellnessScore;
  if (score >= 0.5 && score <= 2) {
    return {
      text: "Your results indicate that your career is in the healthy zone. That's great! Start thinking about how you can sustain this in terms of Career Longevity and Job Happiness.",
      media: { src: "/images/Career Wellness/Healthy.jpg", type: "image" },
      nextSteps: [
        {
          title: "Preserve What's Working",
          body: "Examine why your career satisfaction is high so you can maintain these positive elements. Document what you value most about your current situation to guide future decisions.",
        },
        {
          title: "Explore Growth Opportunities",
          body: "From your position of strength, look for ways to expand your impact or develop in new directions. High career wellness provides a solid foundation for taking calculated risks that could lead to even greater fulfillment.",
        },
        {
          title: "Share Your Positive Practices",
          body: "Mentor others and share the approaches that have helped you achieve career wellness. This not only helps colleagues but reinforces your own positive practices and builds your leadership reputation.",
        },
      ],
    };
  } else if (score >= -0.49 && score <= 0.49) {
    return {
      text: "Your results indicate that your career health is in the neutral zone. Ask yourself, why aren't you happier in your career? What areas should you look deeper into, and what changes can you make to improve your wellness score?",
      media: { src: "/images/Career Wellness/Healthy.jpg", type: "image" },
      nextSteps: [
        {
          title: "Build On Your Strengths",
          body: "Identify which aspects of your career are working well and find ways to expand these elements. Do more of the things you find enjoyable and are good at.",
        },
        {
          title: "Focus On Specifics",
          body: "Rather than a complete overhaul, focus on fixing the one or two areas causing the most dissatisfaction. This targeted approach is often more effective than trying to change everything at once",
        },
        {
          title: "Set Meaningful Goals",
          body: "Establish clear career objectives that align with your values and interests. Having purpose and direction can significantly boost your overall career satisfaction even if all aspects aren't perfect.",
        },
      ],
    };
  } else if (score >= -2 && score <= -0.5) {
    return {
      text: "Your results indicate that certain areas need to be further looked into. What key areas of your career are making you unhappy? When did you start feeling this way? How can you improve the situation?",
      media: { src: "/images/Career Wellness/Healthy.jpg", type: "image" },
      nextSteps: [
        {
          title: "Reflect On Your Career",
          body: "Identify specifically what's making you unhappy in your career. Is it your work environment, salary, workload, or lack of purpose? Understanding the root causes will help you address them effectively rather than making hasty decisions.",
        },
        {
          title: "Start Small Improvements",
          body: "Instead of making dramatic changes immediately, look for ways to improve your current situation, eg, having with your manager about workload, seeking new projects that interest you, or setting boundaries around work hours.",
        },
        {
          title: "Speak to Somebody",
          body: "Sometimes, having a listening ear or just getting a different perspective on your situation can help you gain new insights into your career situation.",
        },
      ],
    };
  }
  return "N/A";
};

export const getCareerStageScore = (report) => {
  const score = report.careerStage;
  const stages = {
    2: {
      title: "Accelerator",
      text: "You're ready to elevate your career to the next level and take on greater challenges.\n\nThis ambition is exciting, but requires strategic focus to ensure you're accelerating in the right direction. You've got this!",
      media: { src: "/images/Career Stage/Accelerator 1.png", type: "image" },
      nextSteps: [
        {
          title: "Fill Skill Gaps",
          body: "Identify what capabilities you'll need for your target role and develop a plan to acquire them. Consider cross-functional projects that provide exposure to new areas.",
        },
        {
          title: "Find A Mentor",
          body: "Connect with someone who has successfully navigated the path you're pursuing. Their guidance can help you avoid pitfalls and accelerate your progress.",
        },
        {
          title: "Scan For Opportunities",
          body: "Actively look for ways to expand your responsibilities or domain. Research which skills are growing in demand and where that growth is coming from.",
        },
      ],
    },
    1: {
      title: "Urgent Role-Seeker",
      text: "You seem to be looking for a new role quickly, which can feel overwhelming.\n\nThe good news is that with focused effort, you can significantly improve your chances of success.",
      media: { src: "/images/Career Stage/JobSeeker.png", type: "image" },
      nextSteps: [
        {
          title: "Target Strategically",
          body: "Identify specific roles in organizations where your skills directly address their challenges. Focus your efforts where you can demonstrate clear value rather than applying randomly.",
        },
        {
          title: "Network Proactively",
          body: "Connect directly with decision-makers rather than relying solely on job applications. Find out who makes hiring decisions in your target organizations and develop relationships with them.",
        },
        {
          title: "Refine Your Approach",
          body: "If you're getting interviews but not offers, seek feedback to understand why. Analyze your interview strategy and make necessary adjustments to improve your success rate.",
        },
      ],
    },
    0: {
      title: "Crossroader",
      text: "You're at a significant turning point in your professional journey, feeling unsure which path to take next.\n\nThis uncertainty can be frustrating, but you're not alone - over 80% of executives experience this feeling at some point in their careers.",
      media: { src: "/images/Career Stage/Crossroader.png", type: "image" },
      nextSteps: [
        {
          title: "Reflect Deeply",
          body: "Prioritize what truly matters to you (better pay? more challenges? better work-life balance?) and develop a career strategy based on these priorities.",
        },
        {
          title: "Identify Your Strengths",
          body: "Thoroughly assess what you excel at and enjoy doing. Understanding your unique skills provides important clues about which direction might suit you best.",
        },
        {
          title: "Research Options",
          body: "Actively investigate potential career paths through networking conversations and online research. Gather information about different roles and industries to make an informed decision rather than an emotional one.",
        },
      ],
    },
  };
  return stages[score] || "N/A";
};

export const getCareerVelocityScore = (report) => {
  const score = report.careerVelocity;
  const velocities = {
    2: {
      title: "You Want To Accelerate",
      text: "You plan to progress your career in the near future by gaining more responsibility, earning a higher income, and attaining more authority and influence.\n\nThis requires time, strategy, patience, and often involves enhancing skills and expertise, as well as taking on greater responsibilities.",
      media: { src: "/images/Career Velocity/Accelerate.png", type: "image" },
      nextSteps: [
        {
          title: "Identify Growth Areas",
          body: "Research emerging trends and high-demand skills in your industry that align with your strengths. Focus on developing expertise that positions you for bigger roles.",
        },
        {
          title: "Build Strategic Visibility",
          body: "Volunteer for high-profile projects that showcase your capabilities to senior leadership. Create opportunities to demonstrate your readiness for increased responsibility.",
        },
        {
          title: "Expand Your Network",
          body: "Cultivate relationships with influential figures both inside and outside your organization. Connect with leaders who can advocate for your advancement when opportunities arise.",
        },
      ],
    },
    1: {
      title: "You Want To Hover",
      text: "You intend to stay put at the moment and do not plan on making any bold moves, possibly considering 'lateral' ones that may not entail additional responsibilities.\n\nBut what are your reasons for hovering? How long do you intend to hover, and what are you planning to do once your hovering is done?",
      media: { src: "/images/Career Velocity/Hover.png", type: "image" },
      nextSteps: [
        {
          title: "Maintain Relevance",
          body: 'Even while "hovering," continue updating your skills to avoid becoming outdated. Dedicate time to learning industry developments to preserve your market value.',
        },
        {
          title: "Define Your Timeline",
          body: "Set a specific period for your hovering phase with clear goals. Determine what conditions would signal it's time to shift back into acceleration mode.",
        },
        {
          title: "Add Strategic Value",
          body: "Find ways to deepen your expertise in your current role. Become the go-to resource in your specific area to strengthen your position while maintaining current responsibilities.",
        },
      ],
    },
    0: {
      title: "You Want To Downshift",
      text: "You plan to take things a bit easier from here onwards to slow down your career. This move involves a deliberate reduction of the scale and scope of your work, so you can focus on other priorities in your life.\n\nHave you decided what you are going to do during this quieter period? What does this new phase look like to you and what do you want to do?",
      media: { src: "/images/Career Velocity/Downshift.png", type: "image" },
      nextSteps: [
        {
          title: "Plan Financially",
          body: "Create a detailed budget that accounts for potential income changes. Ensure your downshift is sustainable for your desired timeframe.",
        },
        {
          title: "Preserve Core Skills",
          body: "Identify and maintain the most valuable professional capabilities that will remain relevant should you decide to upshift again later.",
        },
        {
          title: "Communicate Intentionally",
          body: "Frame your downshift as a strategic choice rather than a step back. Develop a clear narrative that emphasizes the purposeful nature of your decision to colleagues and future employers.",
        },
      ],
    },
  };
  return velocities[score] || "N/A";
};

export const getCareerDirectionScore = (report) => {
  const score = report.careerPivots;
  const directions = {
    1: {
      title: "You Want To Stay Put",
      text: "You've decided that for your next move, you intend to stay put, remain in the same Business Unit or Industry, in and the same role. This is the safest and least risky move to make.\n\nWhile remaining in place, how else can you upgrade your capabilities so that you can do your job better? What skills can you learn to be more indispensible to your team?",
      media: {
        src: "/images/Career Direction/Career Direction Actual.png",
        type: "image",
      },
      nextSteps: [
        {
          title: "Identify Reasons Why",
          body: "Examine your reasons for staying in this comfort zone. Is it because you're not ready to move yet, or perhaps this is an area you are still passionate about?",
        },
        {
          title: "Determine Sustainability",
          body: "Is this market or role growing, or is it in decline? If the role or department or sector is slated for outsourcing, automation or AI-replacement, consider other pivoting options.",
        },
        {
          title: "Specialise and Deepskill",
          body: "Do not squander the opportunity to become the Expert in this area. Gain experience, expertise and deepen your capabilities whilst growing your reputation in your company and beyond.",
        },
      ],
    },
    2: {
      title: "You Want To Switch Business Unit or Industry",
      text: "You've decided that for your next move, you want to stay in the same role you're in now but pivot to another Business Unit or Industry.\n\nYou now need to focus on building new knowledge and adapting your skills to the new industry.",
      media: {
        src: "/images/Career Direction/Career Direction Actual.png",
        type: "image",
      },
      nextSteps: [
        {
          title: "Identify Transferable Skills",
          body: "List strong career pivots that are transferable and in demand by the industry or business unit you want to move into and leverage strongly on them.",
        },
        {
          title: "Identify Specialised Skills",
          body: "Look at your Superpowers – are there special skills you are very good at that the target Business Unit or Industry might be looking for?",
        },
        {
          title: "Conduct Research",
          body: "Do your homework carefully and network as much into this area before making the leap.",
        },
      ],
    },
    3: {
      title: "You Want To Pivot Into A New Role",
      text: "You've decided that for your next move, you want to stay in your current Business Unit or Industry, but change roles.\n\nLeveraging your depth of experiences and industry knowledge, your learning curve would be considerably shortened and you can transition quickly into the new role.",
      media: {
        src: "/images/Career Direction/Career Direction Actual.png",
        type: "image",
      },
      nextSteps: [
        {
          title: "Identify Industry Knowledge",
          body: "Use your depth of industry knowledge and specific expertise as your selling point. Highlight how you already understand the products, customers, and challenges in this space to enhance your marketability.",
        },
        {
          title: "Identify Sponsors",
          body: "Look for 'Sponsors' in your target areas who know you and would be willing to take you onboard or make a strong recommendation on your behalf.",
        },
        {
          title: "Conduct Research",
          body: "Do your homework carefully and network as much into this area before making the leap.",
        },
      ],
    },
    4: {
      title: "You Want Something Different",
      text: "You've decided that for your next move, you want to pivot into an entirely new area that is different from your current role or industry.\n\nThis is often seen to be the riskiest move because you have to learn everything from scratch and hence, need to embark on proper planning long before you make this shift.",
      media: {
        src: "/images/Career Direction/Career Direction Actual.png",
        type: "image",
      },
      nextSteps: [
        {
          title: "Do not Rush",
          body: "There are many variables involved in this move so do not be hasty or emotional. Take your time, consider all options, and speak to your life-partner to gain their understanding and support.",
        },
        {
          title: "Network Aggressively",
          body: "Find mentors, advisors and even coaches who can deepen your understanding of this new area. Leverage them to find inroads and opportunities to accelerate and smoothen your transition.",
        },
        {
          title: "Conduct Research",
          body: "Do your homework carefully and network as much into this area before making the leap.",
        },
      ],
    },
  };
  return directions[score] || "N/A";
};

export const getCareerStrategyScore = (report) => {
  const score = report.careerStrategyScore;
  if (score >= 0.5 && score <= 2) {
    return {
      text: "Your score was found to be above industry average! Continue developing your Career Strategy further to ensure career satisfaction and longevity.",
      media: { src: "/images/Career Strategy/New Project.png", type: "image" },
      nextSteps: [
        {
          title: "Test Your Assumptions",
          body: "While you have a solid plan, the market changes quickly. Regularly challenge your own assumptions about industry direction, skill demands, and career pathways.",
        },
        {
          title: "Consider 'What-if's",
          body: "Create contingency strategies for unexpected changes. What's your Plan B if your industry faces disruption? Don't get caught off guard by sudden shifts.",
        },
        {
          title: "Mentor Others",
          body: "Teaching others about career strategy will reinforce your own thinking while building your network and reputation.",
        },
      ],
    };
  } else if (score >= -0.49 && score <= 0.49) {
    return {
      text: "Your score was found to be at industry average. Learn how to develop your Career Strategy further to ensure better career longevity and sustainability.",
      media: { src: "/images/Career Strategy/New Project.png", type: "image" },
      nextSteps: [
        {
          title: "Fill In The Gaps",
          body: "Identify specifically where your strategy needs strengthening - is it timeline clarity, skill development plans, or industry understanding?",
        },
        {
          title: "Research Industry Trends",
          body: "Deepen your understanding of where your field is heading. Read industry publications, attend conferences, or network with thought leaders to refine your existing strategy.",
        },
        {
          title: "Check & Review",
          body: "Create specific checkpoints to review your progress. Schedule quarterly strategy reviews and adjust your plans based on changing conditions or new information.",
        },
      ],
    };
  } else if (score >= -2 && score <= -0.5) {
    return {
      text: "Your Career Strategy score was found to be below industry average, and this could hinder your career planning and development in the long run if you are not careful.",
      media: { src: "/images/Career Strategy/New Project.png", type: "image" },
      nextSteps: [
        {
          title: "Start With Self-assessment",
          body: "Take time to reflect on what you truly want from your career. What are your strengths, values, and long-term goals? What do you REALLY want?",
        },
        {
          title: "Create A Basic Roadmap",
          body: "Develop a simple plan with clear milestones. Where do you want to be in 5 years, and start mapping your next steps. Identify skills and experience gaps.",
        },
        {
          title: "Find A Mentor or Coach",
          body: "Connect with someone who has experience navigating career paths successfully who can provide guidance, share their experiences, and help you develop a more robust strategy.",
        },
      ],
    };
  }
  return "N/A";
};

export const getCareerAgilityScore = (report) => {
  const score = report.careerAgilityScore;
  if (score >= 0.5 && score <= 2) {
    return {
      text: "You have a strong grasp of market trends, stay ahead of industry shifts, and are well-prepared for career opportunities or disruptions. Your flexibility and openness to different options give you a distinct advantage.",
      media: {
        src: "/images/Career Agility/Career Agility.jpg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Challenge Yourself!",
          body: "Look beyond current trends to identify emerging fields. Position yourself at the forefront of industry evolution rather than just keeping pace.",
        },
        {
          title: "Build Strategic Partnerships",
          body: "Form alliances with other highly agile professionals. These relationships will provide early access to opportunities and insights that others might miss.",
        },
        {
          title: "Mentor Others In Adaptability",
          body: "Coach colleagues on developing career agility while reinforcing your own skills. Teaching others will solidify your expertise.",
        },
      ],
    };
  } else if (score >= -0.49 && score <= 0.49) {
    return {
      text: "You have a fair grasp of market trends, may be skilled at what you do, but may not be fully prepared for new career opportunities or disruptions.",
      media: {
        src: "/images/Career Agility/Career Agility.jpg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Cross-Skill Yourself",
          body: "Build knowledge in adjacent areas to your current role. Understanding related fields makes you more versatile when opportunities or changes arise.",
        },
        {
          title: "Create A Trend Radar",
          body: "Set up a system to monitor industry changes and emerging opportunities. Subscribe to relevant publications and set aside time monthly to assess how these trends might affect your career.",
        },
        {
          title: "Strengthen Your Professional Brand",
          body: "Clearly communicate your unique value proposition. Make sure others know your specialized skills so opportunities find you naturally.",
        },
      ],
    };
  } else if (score >= -2 && score <= -0.5) {
    return {
      text: "Are you up to date on market trends, proficient with the latest skills needed and well networked to access opportunities? Are you REALLY ready for the changes ahead?",
      media: {
        src: "/images/Career Agility/Career Agility.jpg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Update Your Skills Regularly",
          body: "Schedule monthly learning activities focused on in-demand skills in your field. Choose one emerging technology or methodology to master each quarter.",
        },
        {
          title: "Expand Your Professional Network",
          body: "Connect with people outside your immediate team or company. Make it a habit to have coffee with someone new in your industry at least once a month.",
        },
        {
          title: "Practice Adaptability",
          body: "Volunteer for projects that push you out of your comfort zone. Embrace change rather than resisting it to build your adaptability muscles.",
        },
      ],
    };
  }
  return "N/A";
};

export const getSalarySatisfactionScore = (report) => {
  const score = report.salarySatisfaction;
  if (score >= 0.5 && score <= 2) {
    return {
      text: "You feel well-compensated for your work and believe your salary reflects your skills, experience, and contributions. Money isn't a major concern, allowing you to focus on career growth and job fulfillment.",
      media: {
        src: "/images/Salary Satisfaction/Happy - Good Salary.jpeg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Focus On Growth Opportunities",
          body: "Look beyond compensation to develop skills and experiences that will sustain your long-term career value and satisfaction.",
        },
        {
          title: "Invest In Your Future",
          body: "Use your financial comfort to pursue advanced education, certifications, or side projects that align with your interests and career goals.",
        },
        {
          title: "Identify Specific Value Gaps",
          body: "Determine whether you need more recognition, responsibility, flexibility, or actual compensation to feel fairly rewarded for your contributions.",
        },
      ],
    };
  } else if (score >= -0.49 && score <= 0.49) {
    return {
      text: "You feel fairly satisfied with your salary but maybe, there could be room for improvement. While it meets your basic needs, you may sometimes question if you're being paid what you're truly worth.",
      media: {
        src: "/images/Salary Satisfaction/Happy - Good Salary.jpeg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Identify Specific Value Gaps",
          body: "Determine whether you need more recognition, responsibility, flexibility, or actual compensation to feel fairly rewarded for your contributions.",
        },
        {
          title: "Build A Business Case",
          body: "Document your achievements, market value, and contributions to prepare for compensation discussions with concrete examples of your impact.",
        },
        {
          title: "Focus On Growth Opportunities",
          body: "Look beyond compensation to develop skills and experiences that will sustain your long-term career value and satisfaction.",
        },
      ],
    };
  } else if (score >= -2 && score <= -0.5) {
    return {
      text: "You seem to feel underpaid for your work and this could lead to unhappiness. You feel your salary doesn't fully align with your expectations and have been considering steps to improve your earnings.",
      media: {
        src: "/images/Salary Satisfaction/Happy - Good Salary.jpeg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Research Market Rates",
          body: "Gather concrete data on industry compensation standards for your role, experience level, and location to understand if your expectations are realistic.",
        },
        {
          title: "Enhance Your Skills Portfolio",
          body: "Identify and develop high-demand skills that command premium compensation in your field or adjacent areas with better earning potential.",
        },
        {
          title: "Prepare For Crucial Conversations",
          body: "Practice discussing compensation professionally, focusing on your value rather than personal needs, and be ready with a specific request based on research.",
        },
      ],
    };
  }
  return "N/A";
};

export const getWorkEnvironmentSatisfactionScore = (report) => {
  const score = report.workEnvironmentSatisfaction;
  if (score >= 0.5 && score <= 2) {
    return {
      text: "You seem very happy with the people your workplace. This indicates positive, supportive relationships at work and a healthy environment to be in.",
      media: {
        src: "/images/Work Environment Satisfaction/doctor.png",
        type: "image",
      },
      nextSteps: [
        {
          title: "Leverage Strong Relationships",
          body: "Use your positive connections with colleagues and leadership to gain access to challenging projects and growth opportunities that can advance your career.",
        },
        {
          title: "Extend Your Reach",
          body: "Share insights about effective workplace dynamics with other departments or teams to expand your influence while strengthening the broader organization.",
        },
        {
          title: "Champion Team Culture",
          body: "Take an active role in preserving the positive aspects of your work environment by mentoring new team members and reinforcing collaborative practices.",
        },
      ],
    };
  } else if (score >= -0.49 && score <= 0.49) {
    return {
      text: "You seem somewhat satisfied with the people your workplace. This indicates positive, supportive relationships at work and a healthy environment to be in.",
      media: {
        src: "/images/Work Environment Satisfaction/doctor.png",
        type: "image",
      },
      nextSteps: [
        {
          title: "Identify Relationship Patterns",
          body: "Analyze which workplace relationships energize you versus those that drain you, then strategically adjust your interaction approaches accordingly.",
        },
        {
          title: "Build Bridges Strategically",
          body: "Develop stronger connections with influential colleagues or leaders whose work styles complement yours to create supportive micro-environments.",
        },
        {
          title: "Clarify Communication Preferences",
          body: "Have direct conversations about how you prefer to receive feedback, collaborate on projects, and communicate with your boss and key team members.",
        },
      ],
    };
  } else if (score >= -2 && score <= -0.5) {
    return {
      text: "You seem somewhat disatisfied with some people your workplace. This might suggest interpersonal challenges or a poor cultural fit that could affect your career satisfaction and performance.",
      media: {
        src: "/images/Work Environment Satisfaction/doctor.png",
        type: "image",
      },
      nextSteps: [
        {
          title: "Document Specific Interactions",
          body: "Keep a record of problematic workplace dynamics to identify patterns and develop targeted improvement strategies rather than generalizing frustrations.",
        },
        {
          title: "Establish Professional Boundaries",
          body: "Clearly communicate your work style needs and limits while maintaining professionalism, particularly with difficult colleagues or managers.",
        },
        {
          title: "Expand Your Internal Network",
          body: "Connect with colleagues from other departments who might offer perspective, support, or potentially provide pathways to team transfers if problems persist.",
        },
      ],
    };
  }
  return "N/A";
};

export const getWorkContentSatisfactionScore = (report) => {
  const score = report.workContentSatisfaction;
  if (score >= 0.5 && score <= 2) {
    return {
      text: "You truly enjoy the work you do every day. You find your tasks meaningful, engaging, and well-aligned with your skills and interests. This is a significant career advantage that many people never achieve.",
      media: {
        src: "/images/Work Content Satisfaction/Work Content Satisfaction.jpeg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Deepen Your Expertise",
          body: "Invest in becoming even more knowledgeable in your field through specialized training or challenging projects that stretch your capabilities.",
        },
        {
          title: "Share Your Knowledge",
          body: "Look for opportunities to mentor others or contribute to your field through writing, speaking, or teaching to amplify your impact and visibility.",
        },
        {
          title: "Explore Related Growth Areas",
          body: "Identify adjacent skills or responsibilities that could expand your current role while maintaining the core work you already enjoy.",
        },
      ],
    };
  } else if (score >= -0.49 && score <= 0.49) {
    return {
      text: "You find aspects of your work engaging but other parts may feel tedious or misaligned with your interests. There's potential to reshape your role to increase your overall satisfaction.",
      media: {
        src: "/images/Work Content Satisfaction/Work Content Satisfaction.jpeg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Identify Your Engagement Patterns",
          body: "Track which specific tasks energize you versus those that drain you, then look for opportunities to do more of what you enjoy.",
        },
        {
          title: "Redesign Your Role",
          body: "Have a constructive conversation with your manager about adjusting your responsibilities to better leverage your strengths and interests.",
        },
        {
          title: "Develop Efficiency Strategies",
          body: "Create systems to complete necessary but less enjoyable tasks more effectively, freeing up time for work you find more meaningful.",
        },
      ],
    };
  } else if (score >= -2 && score <= -0.5) {
    return {
      text: "You likely feel disconnected from your daily work tasks, finding them unfulfilling, boring, or misaligned with your skills and values. This situation could be draining your energy and affecting your overall career satisfaction.",
      media: {
        src: "/images/Work Content Satisfaction/Work Content Satisfaction.jpeg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Clarify Your Work Values",
          body: "Reflect deeply on what type of work would feel meaningful to you, considering your interests, skills, and what impact you want to make.",
        },
        {
          title: "Seek New Challenges",
          body: "Volunteer for different projects or responsibilities within your current organization that might better align with your interests and abilities.",
        },
        {
          title: "Explore Alternative Paths",
          body: "Research roles or fields that better match your preferred work content while leveraging your transferable skills, possibly beginning with informational interviews.",
        },
      ],
    };
  }
  return "N/A";
};

export const getWorkLifeBalanceSatisfactionScore = (report) => {
  const score = report.worklifeBalanceSatisfaction;
  if (score >= 0.5 && score <= 2) {
    return {
      text: "You've successfully created healthy boundaries between your professional and personal life. You have time for family, personal interests, and proper rest while still performing well at work, contributing to good career sustainability.",
      media: {
        src: "/images/Work-life Balance Satisfaction/Worklife Balance.jpg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Protect Your Boundaries",
          body: "Maintain the systems and limits you've established by continuing to communicate your availability clearly and respectfully declining requests that compromise your balance.",
        },
        {
          title: "Share Effective Practices",
          body: "Consider mentoring others on how you achieve balance, which will strengthen your leadership reputation while helping create a healthier workplace culture.",
        },
        {
          title: "Review Regularly",
          body: "Schedule quarterly check-ins with yourself to ensure your balance remains appropriate as career demands and personal circumstances evolve.",
        },
      ],
    };
  } else if (score >= -0.49 && score <= 0.49) {
    return {
      text: "You have some structure between work and personal life, but the boundaries occasionally blur. Some weeks feel manageable while others leave you depleted. There's room to create more consistent balance that supports both your career and personal wellbeing.",
      media: {
        src: "/images/Work-life Balance Satisfaction/Worklife Balance.jpg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Identify Specific Pressure Points",
          body: "Track exactly when and how work tends to encroach on your personal time to develop targeted solutions rather than general boundaries.",
        },
        {
          title: "Establish Clear Protocols",
          body: "Create and communicate specific guidelines about your availability outside work hours, response times to messages, and how emergencies should be handled.",
        },
        {
          title: "Decompress Regularlty",
          body: "Develop consistent routines that help you mentally shift between work and personal modes, such as end-of-day wrap-up practices or physical activities.",
        },
      ],
    };
  } else if (score >= -2 && score <= -0.5) {
    return {
      text: "Your work responsibilities may have encroached on your personal life and may be experiencing early symptoms of burnout. If left unchecked, this situation will be unsustainable and requires attention.",
      media: {
        src: "/images/Work-life Balance Satisfaction/Worklife Balance.jpg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Conduct An Urgent Assessment",
          body: "Evaluate whether your workload truly requires your current hours or if perfectionism, inefficiency, or unclear expectations are contributing factors.",
        },
        {
          title: "Have Critical Conversations",
          body: "Discuss workload concerns with your manager using specific examples and data, focusing on how improved balance will benefit both you and the organization.",
        },
        {
          title: "Decompress Regularlty",
          body: "Develop consistent routines that help you mentally shift between work and personal modes, such as end-of-day wrap-up practices or physical activities.",
        },
      ],
    };
  }
  return "N/A";
};

export const getCurrentWorkloadSatisfactionScore = (report) => {
  const score = report.satisfactionWithCurrentWorkload;
  if (score >= 0.5 && score <= 2) {
    return {
      text: "You feel comfortable with your current work volume and responsibilities. The amount of work you have is challenging but manageable. This balance contributes significantly to your overall job satisfaction and performance.",
      media: {
        src: "/images/Satisfaction with Current Workload/Workload.jpg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Consider Strategic Growth",
          body: "Explore taking on additional high-impact projects that align with your career goals while your current workload remains manageable.",
        },
        {
          title: "Optimize Your Systems",
          body: "Document the processes and approaches that help you manage your workload effectively so you can maintain this balance as projects or responsibilities change.",
        },
        {
          title: "Share Best Practices",
          body: "Offer to mentor colleagues who struggle with workload management, strengthening your leadership skills while improving team efficiency.",
        },
      ],
    };
  } else if (score >= -0.49 && score <= 0.49) {
    return {
      text: "You're managing your current workload but occasionally feel stretched or under pressure. There are periods where the volume of work becomes challenging, though generally you're able to cope.",
      media: {
        src: "/images/Satisfaction with Current Workload/Workload.jpg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Identify Peak Periods",
          body: "Map out when workload typically increases and prepare strategies in advance. Try negotiating for more support during peak periods.",
        },
        {
          title: "Reprioritise Tasks",
          body: "Review the Urgent/Important nature of tasks and engage your stakeholders to understand how they determine which work truly matters most. Prioritise accordingly.",
        },
        {
          title: "Negotiate Realistic Deadlines",
          body: "Proactively discuss timeline expectations with your boss or colleagues before committing to new responsibilities.",
        },
      ],
    };
  } else if (score >= -2 && score <= -0.5) {
    return {
      text: "You're consistently feeling overwhelmed by your current workload. The volume of tasks and responsibilities exceeds your capacity, creating stress and potentially affecting your performance and wellbeing.",
      media: {
        src: "/images/Satisfaction with Current Workload/Workload.jpg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Review Action Items",
          body: "Create a comprehensive inventory of all tasks, time requirements, and deadlines. Is everything you're doing now part of your job, or someone else's?",
        },
        {
          title: "Initiate A Workload Discussion",
          body: "Schedule a meeting with your manager to review your workload and explore solutions. It could be in the form of more support, better tools, or more reasonable deadlines.",
        },
        {
          title: "Establish Clear Boundaries",
          body: "Define non-negotiable limits on overtime, weekend work, and after-hours communications.",
        },
      ],
    };
  }
  return "N/A";
};

export const getJobSecuritySatisfactionScore = (report) => {
  const score = report.jobSecurity;
  if (score >= 0.5 && score <= 2) {
    return {
      text: "You feel very confident about your position within the organization and see little risk of involuntary job loss in the near future.",
      media: { src: "/images/Job Security/Job Security.jpeg", type: "image" },
      nextSteps: [
        {
          title: "Continue Demonstrating Your Value",
          body: "Regularly highlight your contributions and achievements to maintain visibility with leadership and reinforce your importance to the organization",
        },
        {
          title: "Stay Alert To Industry Changes",
          body: "Monitor emerging trends and technologies that could impact your role or industry, even when things seem stable",
        },
        {
          title: "Expand Your Internal Network",
          body: "Build relationships across different departments to increase your awareness of company-wide opportunities and potential future directions",
        },
      ],
    };
  } else if (score >= -0.49 && score <= 0.49) {
    return {
      text: "You feel somewhat secure in your current role but have some concerns or uncertainties about long-term stability.",
      media: { src: "/images/Job Security/Job Security.jpeg", type: "image" },
      nextSteps: [
        {
          title: "Increase Your Professional Visibility",
          body: "Take on high-impact projects and ensure key decision-makers are aware of your contributions and successes",
        },
        {
          title: "Develop Adaptable, In-Demand Skills",
          body: "Identify and acquire skills that would be valuable across multiple roles or departments within your organization",
        },
        {
          title: "Create Connection With Multiple Stakeholders",
          body: "Build relationships with leaders in various parts of the organization to expand your internal opportunities and support network",
        },
      ],
    };
  } else if (score >= -2 && score <= -0.5) {
    return {
      text: "You have significant concerns about the stability of your position and feel that your employment may be at risk.",
      media: { src: "/images/Job Security/Job Security.jpeg", type: "image" },
      nextSteps: [
        {
          title: "Develop A Comprehensive Contingency Plan",
          body: "Create an action strategy that includes updated resume, refreshed LinkedIn profile, financial planning, and identified target companies.",
        },
        {
          title: "Enhance Your Market Value",
          body: "Immediately begin acquiring certifications or skills that are in high demand in your industry or adjacent fields.",
        },
        {
          title: "Activate Your External Network",
          body: "Reconnect with professional contacts outside your current organization and begin exploratory conversations about potential opportunities.",
        },
      ],
    };
  }
  return "N/A";
};
const getSkillsClarityText = (report) => {
  const score = report.skillsClarity;
  if (score >= -2 && score <= -0.5) {
    return {
      text: "Your Skills Agility score is worryingly low. You're unable to describe what you're good at, what skills your market is looking for, and sometimes, you wonder if you're as skilled as your peers. Worse still, you may not have been upskilling regularly. This could lead to skills obsolescence.",
      media: {
        src: "/images/Skills Clarity/Skills 2.jpg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Assess Your Current Skills",
          body: "Find out what skills the market needs, and start planning on acquiring them.",
        },
        {
          title: "Seek Expert Guidance",
          body: "Speak to your bosses or experts in the field to get their points of view.",
        },
        {
          title: "Create Development Plan",
          body: "Make a concrete plan to address your skill gaps and set timelines for achievement.",
        },
      ],
    };
  } else if (score >= -0.49 && score <= 0.49) {
    return {
      text: "Your Skills Agility score is average, but being average in this competitive environment is not good enough. You will need to constantly acquire new and relevant skills, get better at the skills you already have, find out what future skills the market will need in the coming years, and master them!",
      media: {
        src: "/images/Skills Clarity/Skills 2.jpg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Plan Your Skill Development",
          body: "Make a list of skills you want to acquire in the next year.",
        },
        {
          title: "Find Learning Opportunities",
          body: "Research courses, certifications, or mentorship opportunities that align with your skill goals.",
        },
        {
          title: "Track Your Progress",
          body: "Set up regular check-ins to monitor your skill development progress.",
        },
      ],
    };
  } else if (score >= 0.5 && score <= 2) {
    return {
      text: "Your Skills Agility score is High - Congratulations! But watch out, your peers are catching up to you, so you have to keep upskilling. Constantly acquire new and relevant skills, get better at the skills you already have, find out what future skills the market will need in the coming years, and master them!",
      media: {
        src: "/images/Skills Clarity/Skills 2.jpg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Maintain Your Edge",
          body: "Seek to gain mastery of the skills you already have.",
        },
        {
          title: "Future-Proof Your Skills",
          body: "Make a list of skills you want to acquire in the next year to stay ahead.",
        },
        {
          title: "Share Your Knowledge",
          body: "Consider mentoring others while continuing to enhance your own expertise.",
        },
      ],
    };
  } else {
    return "N/A";
  }
};
export const getNetworkingProficiencyScore = (report) => {
  const score = report.networkingProficiency;
  if (score >= 0.5 && score <= 2) {
    return {
      text: "You have a strong professional network and actively maintain relationships that could lead to career opportunities. You're comfortable networking and understand its value in career development.",
      media: {
        src: "/images/Networking Proficiency/Networking Proficiency.jpeg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Leverage Your Network Strategically",
          body: "Use your network to gain industry insights, mentorship opportunities, and potential career advancement paths",
        },
        {
          title: "Give Back To Your Network",
          body: "Look for opportunities to help others in your network, strengthening relationships and building goodwill",
        },
        {
          title: "Expand Into New Areas",
          body: "Branch out into new industry sectors or professional groups to diversify your network and exposure",
        },
      ],
    };
  } else if (score >= -0.49 && score <= 0.49) {
    return {
      text: "You have a moderate professional network but may not be fully leveraging it for career opportunities. There's room to improve both the size and quality of your network.",
      media: {
        src: "/images/Networking Proficiency/Networking Proficiency.jpeg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Set Networking Goals",
          body: "Create specific, measurable objectives for expanding your network and deepening existing relationships",
        },
        {
          title: "Develop Your Networking Strategy",
          body: "Plan regular check-ins with key contacts and identify relevant industry events or professional groups to join",
        },
        {
          title: "Practice Relationship Building",
          body: "Focus on creating meaningful connections rather than just collecting contacts",
        },
      ],
    };
  } else if (score >= -2 && score <= -0.5) {
    return {
      text: "Your professional network needs significant development. You may feel uncomfortable networking or haven't prioritized building professional relationships.",
      media: {
        src: "/images/Networking Proficiency/Networking Proficiency.jpeg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Start Small But Consistent",
          body: "Begin with reconnecting with former colleagues and classmates through professional social media",
        },
        {
          title: "Join Professional Communities",
          body: "Identify and join relevant professional groups or online communities in your field",
        },
        {
          title: "Create A Networking System",
          body: "Develop a simple system to track contacts and set reminders for regular follow-ups",
        },
      ],
    };
  }
  return "N/A";
};

export const getCareerDisruptionReadinessScore = (report) => {
  const score = report.careerDisruptionReadiness;
  if (score >= 0.5 && score <= 2) {
    return {
      text: "You are well-prepared for potential career disruptions and have systems in place to handle unexpected changes. Your adaptability and preparation give you a strong foundation for navigating career transitions.",
      media: {
        src: "/images/Career-Disruption Readiness/Disruption Ahead.jpg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Maintain Your Emergency Fund",
          body: "Regularly review and adjust your financial safety net to ensure it remains adequate for your current lifestyle",
        },
        {
          title: "Keep Skills Current",
          body: "Continue updating your skills portfolio with emerging technologies and industry-relevant competencies",
        },
        {
          title: "Review And Update Plans",
          body: "Periodically review your career contingency plans and update them based on industry changes and personal growth",
        },
      ],
    };
  } else if (score >= -0.49 && score <= 0.49) {
    return {
      text: "You have some preparation for career disruptions but could benefit from more robust planning. There are gaps in your readiness that could be addressed to improve your resilience.",
      media: {
        src: "/images/Career-Disruption Readiness/Disruption Ahead.jpg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Build Your Safety Net",
          body: "Start or increase your emergency fund to cover 3-6 months of expenses",
        },
        {
          title: "Develop Contingency Plans",
          body: "Create specific action plans for different career disruption scenarios",
        },
        {
          title: "Identify Skill Gaps",
          body: "Assess which skills would make you more resilient to career disruptions and create a plan to acquire them",
        },
      ],
    };
  } else if (score >= -2 && score <= -0.5) {
    return {
      text: "Your preparation for career disruptions needs significant improvement. You may be vulnerable to unexpected career changes and would benefit from developing more robust contingency plans.",
      media: {
        src: "/images/Career-Disruption Readiness/Disruption Ahead.jpg",
        type: "image",
      },
      nextSteps: [
        {
          title: "Start Emergency Planning",
          body: "Begin building an emergency fund and identify immediate steps to reduce financial vulnerability",
        },
        {
          title: "Assess Current Position",
          body: "Evaluate your current role's stability and identify potential risks or changes that could affect your position",
        },
        {
          title: "Create Basic Safety Nets",
          body: "Update your resume, LinkedIn profile, and professional network as initial steps toward better preparation",
        },
      ],
    };
  }
  return "N/A";
};

export const getLinkedInReadinessScore = (report) => {
  const score = report.linkedInPreparedness;
  if (score >= 0.5 && score <= 2) {
    return {
      text: "Your LinkedIn profile is well-optimized and effectively showcases your professional brand. You actively use the platform for networking and career development.",
      media: {
        src: "/images/LinkedIn Preparedness/Linkedin.png",
        type: "image",
      },
      nextSteps: [
        {
          title: "Optimize For Visibility",
          body: "Regularly update your profile with keywords relevant to your industry and career goals",
        },
        {
          title: "Share Your Expertise",
          body: "Create and share content that demonstrates your professional knowledge and insights",
        },
        {
          title: "Engage Strategically",
          body: "Participate in relevant industry discussions and connect with thought leaders in your field",
        },
      ],
    };
  } else if (score >= -0.49 && score <= 0.49) {
    return {
      text: "Your LinkedIn profile has the basics covered but could be more impactful. There's room to improve your professional presence and network engagement on the platform.",
      media: {
        src: "/images/LinkedIn Preparedness/Linkedin.png",
        type: "image",
      },
      nextSteps: [
        {
          title: "Enhance Your Profile",
          body: "Add rich media, detailed experience descriptions, and relevant skills to your profile",
        },
        {
          title: "Build Your Network",
          body: "Connect with colleagues, industry peers, and join relevant professional groups",
        },
        {
          title: "Increase Your Activity",
          body: "Start engaging with industry content and sharing your professional insights",
        },
      ],
    };
  } else if (score >= -2 && score <= -0.5) {
    return {
      text: "Your LinkedIn profile needs significant improvement to effectively support your career goals. You may be missing opportunities due to limited presence on the platform.",
      media: {
        src: "/images/LinkedIn Preparedness/Linkedin.png",
        type: "image",
      },
      nextSteps: [
        {
          title: "Complete Your Profile",
          body: "Ensure all basic sections are filled out with professional photo, summary, and experience details",
        },
        {
          title: "Start Networking",
          body: "Begin connecting with current and former colleagues, and join relevant professional groups",
        },
        {
          title: "Learn Platform Features",
          body: "Familiarize yourself with LinkedIn's key features and best practices for professional networking",
        },
      ],
    };
  }
  return "N/A";
};

export const getCVReadinessScore = (report) => {
  const score = report.resumePreparedness;
  if (score >= 0.5 && score <= 2) {
    return {
      text: "Your CV/resume is well-crafted and effectively communicates your professional value. It's regularly updated and tailored to your career goals.",
      media: { src: "/images/Resume Preparedness/Resume.jpg", type: "image" },
      nextSteps: [
        {
          title: "Keep Achievements Current",
          body: "Regularly update your CV with new accomplishments, skills, and quantifiable results",
        },
        {
          title: "Maintain Multiple Versions",
          body: "Create and maintain different versions of your CV tailored to specific career paths or industries",
        },
        {
          title: "Gather Success Stories",
          body: "Document your achievements and impact metrics for future CV updates",
        },
      ],
    };
  } else if (score >= -0.49 && score <= 0.49) {
    return {
      text: "Your CV/resume has the essential elements but could be more compelling. There's room to improve how you present your experience and achievements.",
      media: { src: "/images/Resume Preparedness/Resume.jpg", type: "image" },
      nextSteps: [
        {
          title: "Add Impact Metrics",
          body: "Include specific, quantifiable achievements and results from your experience",
        },
        {
          title: "Update Format And Style",
          body: "Ensure your CV follows current best practices for layout and presentation",
        },
        {
          title: "Tailor Your Content",
          body: "Customize your CV to align with your target roles and industry requirements",
        },
      ],
    };
  } else if (score >= -2 && score <= -0.5) {
    return {
      text: "Your CV/resume needs significant improvement to effectively showcase your professional value. It may be outdated or missing key elements.",
      media: { src: "/images/Resume Preparedness/Resume.jpg", type: "image" },
      nextSteps: [
        {
          title: "Complete Basic Information",
          body: "Ensure all essential sections are included and properly formatted",
        },
        {
          title: "Focus On Achievements",
          body: "Replace job descriptions with specific accomplishments and results",
        },
        {
          title: "Seek Professional Review",
          body: "Get feedback from mentors or career professionals on your CV content and format",
        },
      ],
    };
  }
  return "N/A";
};

// Example of the updated structure for a card with next steps

// Update the getCardData function to include all cards
export const getCardData = (report) => {
  if (!report) return [];

  const cards = {
    // Career Overview
    careerAnimal: {
      ...getCareerAvatar(report),
      category: "careerOverview",
      titleDescription:
        "This is a virtual description of your current career profile.",
      showStars: false,
    },
    careerHappiness: {
      title: "Career Happiness",
      ...getCareerHappinessScore(report),
      category: "careerOverview",
      titleDescription:
        "This measures your overall happiness with your current job.",
      showStars: true,
      score: report.overallJobHappinessScore,
    },
    careerWellness: {
      title: "Career Wellness",
      ...getCareerWellnessScore(report),
      category: "careerOverview",
      titleDescription: "This measures your overall career health.",
      showStars: true,
      score: report.overallCareerWellnessScore,
    },

    // Navigate Your Career
    careerStage: {
      title: "Career Stage",
      ...getCareerStageScore(report),
      category: "navigateCareer",
      titleDescription: "This describes your current career stage.",
      showStars: false,
    },
    careerVelocity: {
      title: "Career Velocity",
      ...getCareerVelocityScore(report),
      category: "navigateCareer",
      titleDescription:
        "This describes the pace of your next few career moves.",
      showStars: false,
    },
    careerDirection: {
      title: "Career Direction",
      ...getCareerDirectionScore(report),
      category: "navigateCareer",
      titleDescription:
        "This describes what your next move is most likely to be.",
      showStars: false,
    },
    careerStrategy: {
      title: "Career Strategy",
      ...getCareerStrategyScore(report),
      category: "navigateCareer",
      titleDescription:
        "This measures how well prepared you are for your next career moves.",
      showStars: true,
      score: report.careerStrategyScore,
    },
    careerAgility: {
      title: "Career Agility",
      ...getCareerAgilityScore(report),
      category: "navigateCareer",
      titleDescription:
        "This measures your ability to respond to opportunities and threats.",
      showStars: true,
      score: report.careerAgilityScore,
    },

    // Thriving at Work
    salarySatisfaction: {
      title: "Salary Satisfaction",
      ...getSalarySatisfactionScore(report),
      category: "thrivingAtWork",
      titleDescription:
        "This measures your satisfaction with your current compensation.",
      showStars: true,
      score: report.salarySatisfaction,
    },
    workEnvironmentSatisfaction: {
      title: "Work Environment Satisfaction",
      ...getWorkEnvironmentSatisfactionScore(report),
      category: "thrivingAtWork",
      titleDescription:
        "This measures how you feel about your workplace environment.",
      showStars: true,
      score: report.workEnvironmentSatisfaction,
    },
    workContentSatisfaction: {
      title: "Work Content Satisfaction",
      ...getWorkContentSatisfactionScore(report),
      category: "thrivingAtWork",
      titleDescription: "This measures how you feel about your work content.",
      showStars: true,
      score: report.workContentSatisfaction,
    },
    workLifeBalanceSatisfaction: {
      title: "Work-Life Balance",
      ...getWorkLifeBalanceSatisfactionScore(report),
      category: "thrivingAtWork",
      titleDescription: "This measures your work-life balance satisfaction.",
      showStars: true,
      score: report.worklifeBalanceSatisfaction,
    },
    currentWorkloadSatisfaction: {
      title: "Current Workload",
      ...getCurrentWorkloadSatisfactionScore(report),
      category: "thrivingAtWork",
      titleDescription:
        "This measures your satisfaction with your current workload.",
      showStars: true,
      score: report.satisfactionWithCurrentWorkload,
    },
    jobSecuritySatisfaction: {
      title: "Job Security",
      ...getJobSecuritySatisfactionScore(report),
      category: "thrivingAtWork",
      titleDescription: "This measures your feeling of job security.",
      showStars: true,
      score: report.jobSecurity,
    },

    // Growing Your Career
    skillsClarity: {
      title: "Skills Clarity",
      ...getSkillsClarityText(report),
      category: "growingCareer",
      titleDescription: "This measures how well-skilled you are for your role.",
      showStars: true,
      score: report.skillsClarity,
    },
    networkingProficiency: {
      title: "Networking Proficiency",
      ...getNetworkingProficiencyScore(report),
      category: "growingCareer",
      titleDescription: "This measures your networking effectiveness.",
      showStars: true,
      score: report.networkingProficiency,
    },
    careerDisruptionReadiness: {
      title: "Career Disruption Readiness",
      ...getCareerDisruptionReadinessScore(report),
      category: "growingCareer",
      titleDescription: "This measures your readiness for career changes.",
      showStars: true,
      score: report.careerDisruptionReadiness,
    },
    linkedInReadiness: {
      title: "LinkedIn Readiness",
      ...getLinkedInReadinessScore(report),
      category: "growingCareer",
      titleDescription: "This measures your LinkedIn profile preparedness.",
      showStars: true,
      score: report.linkedInPreparedness,
    },
    cvReadiness: {
      title: "CV Readiness",
      ...getCVReadinessScore(report),
      category: "growingCareer",
      titleDescription: "This measures your resume preparedness.",
      showStars: true,
      score: report.resumePreparedness,
    },
  };

  // Process all cards to sanitize text
  Object.keys(cards).forEach((key) => {
    if (cards[key].text) {
      cards[key].text = DOMPurify.sanitize(
        boldText(cards[key].text, targetStrings)
      );
    }
  });

  return cards;
};
// Continue with other getter functions...
